// src/services/chatService.js
import { pb } from "./pocketbase";
import { notificationService } from "./notificationService";

export const DEFAULT_SYSTEM_CHANNELS = [
  {
    name: "geral",
    display_name: "Geral & Avisos",
    module_key: "",
    type: "channel",
    description: "Comunicados gerais da empresa e avisos importantes",
    icon: "Megaphone",
    ordem: 1,
  },
  {
    name: "producao",
    display_name: "Produção & Fábrica",
    module_key: "production",
    type: "channel",
    description: "Alinhamentos de fundição, borracha e fila de fabricação",
    icon: "Factory",
    ordem: 2,
  },
  {
    name: "oficina-bancada",
    display_name: "Oficina & Bancada",
    module_key: "oficina",
    type: "channel",
    description: "Dúvidas de montagem, gravação, polimento e ajustes",
    icon: "Wrench",
    ordem: 3,
  },
  {
    name: "pedidos-sac",
    display_name: "Pedidos & SAC",
    module_key: "orders",
    type: "channel",
    description: "Dúvidas de clientes, alterações de peças e prioridades",
    icon: "Truck",
    ordem: 4,
  },
  {
    name: "pdv-showroom",
    display_name: "PDV & Showroom",
    module_key: "pdv",
    type: "channel",
    description: "Atendimento no balcão e conferência de peças prontas",
    icon: "ShoppingBag",
    ordem: 5,
  },
  {
    name: "expedicao-correios",
    display_name: "Expedição & Correios",
    module_key: "postagem",
    type: "channel",
    description: "Etiquetas, envios, postagens e motoboy",
    icon: "Mail",
    ordem: 6,
  },
  {
    name: "colaboradores-rh",
    display_name: "Colaboradores & RH",
    module_key: "colaboradores",
    type: "channel",
    description: "Escalas, banco de horas e avisos da equipe",
    icon: "User",
    ordem: 7,
  },
];

let isSeedingRunning = false;

export const chatService = {
  // --- 1. CRIAR NOVO CANAL ---
  async createChannel({ name, display_name, module_key = "", description = "", icon = "Megaphone", ordem = 0 }) {
    try {
      const cleanName = (name || display_name || "canal")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9_-]/g, "-");

      const payload = {
        name: cleanName,
        display_name: display_name || name,
        type: "channel",
        description: description || "",
        icon: icon || "Megaphone",
        ordem: Number(ordem) || 0,
        is_private: false,
      };

      if (module_key) {
        payload.module_key = module_key;
      }

      const record = await pb.collection("chat_channels").create(payload);
      return record;
    } catch (e) {
      console.error("❌ [chatService] Erro ao criar canal:", e);
      throw e;
    }
  },

  // --- 2. SEED INICIAL DE CANAIS COM TRAVA DE CONCORRÊNCIA ---
  async seedDefaultChannels() {
    if (isSeedingRunning) return [];
    isSeedingRunning = true;

    try {
      const existing = await pb.collection("chat_channels").getFullList({
        filter: "type = 'channel'",
      }).catch(() => []);

      const existingNames = new Set(existing.map((c) => c.name));
      const createdList = [];

      for (const channel of DEFAULT_SYSTEM_CHANNELS) {
        if (!existingNames.has(channel.name)) {
          try {
            const created = await this.createChannel(channel);
            existingNames.add(channel.name);
            createdList.push(created);
          } catch (err) {
            console.warn(`[chatService] Falha ao criar canal '${channel.name}':`, err);
          }
        }
      }

      return createdList;
    } catch (e) {
      console.warn("⚠️ [chatService] Erro ao seedar canais padrão:", e);
      return [];
    } finally {
      isSeedingRunning = false;
    }
  },

  // --- 3. BUSCAR CANAIS DISPONÍVEIS COM DEDUPLICAÇÃO E LIMPEZA AUTOMÁTICA ---
  async getChannels({ user, activeOperator, hasAccess }) {
    try {
      // 1. Busca todos os canais do tipo 'channel'
      let channelRecords = await pb.collection("chat_channels").getFullList({
        filter: "type = 'channel'",
        sort: "ordem,name",
      }).catch(() => []);

      // Se não houver nenhum canal, roda o seed único
      if (channelRecords.length === 0) {
        await this.seedDefaultChannels();
        channelRecords = await pb.collection("chat_channels").getFullList({
          filter: "type = 'channel'",
          sort: "ordem,name",
        }).catch(() => []);
      }

      // --- DEDUPLICAÇÃO & LIMPEZA DE DUPLICATAS ---
      const seenNames = new Map();
      const uniqueChannels = [];
      const duplicateIdsToDelete = [];

      for (const ch of channelRecords) {
        const key = ch.name || ch.display_name;
        if (!seenNames.has(key)) {
          seenNames.set(key, ch);
          uniqueChannels.push(ch);
        } else {
          // Marca o ID duplicado excedente para exclusão no banco
          duplicateIdsToDelete.push(ch.id);
        }
      }

      // Exclui duplicatas excedentes no PocketBase em background
      if (duplicateIdsToDelete.length > 0) {
        duplicateIdsToDelete.forEach((id) => {
          pb.collection("chat_channels").delete(id).catch(() => {});
        });
      }

      // 2. Filtra por permissão de módulo
      const visibleChannels = uniqueChannels.filter((ch) => {
        if (!ch.module_key || ch.module_key === "") return true; // Canal público geral
        if (user?.role === "master") return true;
        return hasAccess ? hasAccess(ch.module_key) : true;
      });

      // 3. Busca conversas diretas (DMs) do usuário/operador (excluindo canais de IA da listagem)
      let dms = [];
      const dmFilters = [];
      if (user?.id) {
        dmFilters.push(`members_users ~ "${user.id}"`);
      }
      if (activeOperator?.id) {
        dmFilters.push(`members_operators ~ "${activeOperator.id}"`);
      }

      if (dmFilters.length > 0) {
        dms = await pb.collection("chat_channels").getFullList({
          filter: `type = 'direct' && (${dmFilters.join(" || ")})`,
          sort: "-updated",
          expand: "members_users,members_operators",
        }).catch(() => []);
      }

      return {
        channels: visibleChannels,
        dms: dms,
      };
    } catch (error) {
      console.warn("⚠️ [chatService] Erro ao carregar canais:", error);
      return { channels: [], dms: [] };
    }
  },

  // --- 4. BUSCAR MENSAGENS DE UM CANAL ---
  async getMessages(channelId, page = 1, perPage = 50) {
    if (!channelId) return { items: [], totalPages: 1 };

    try {
      const res = await pb.collection("chat_messages").getList(page, perPage, {
        filter: `channel = "${channelId}"`,
        sort: "created",
        expand: "sender_user,sender_operator,mentions_users,mentions_operators,reply_to",
      });

      return {
        items: res.items || [],
        totalPages: res.totalPages || 1,
        totalItems: res.totalItems || 0,
      };
    } catch (error) {
      console.error("❌ [chatService] Erro ao carregar mensagens:", error);
      return { items: [], totalPages: 1, totalItems: 0 };
    }
  },

  // --- 5. PARSER DE MENÇÕES E PEDIDOS ---
  parseMessageContent(text) {
    if (!text) return { mentions: [], orders: [] };

    // Detecta pedidos [722361], #722361 ou # 722361 (de 3 a 10 dígitos)
    const orderMatches = text.match(/\[\s*\d{3,10}\s*\]|#\s*\d{3,10}/g) || [];
    const orders = orderMatches
      .map((m) => m.replace(/[[\]#\s]/g, "").trim())
      .filter((v, i, a) => a.indexOf(v) === i);

    return { orders };
  },

  // --- 6. ENVIAR MENSAGEM ---
  async sendMessage({
    channelId,
    user,
    activeOperator,
    content,
    files = [],
    replyToId = null,
    mentionsUsers = [],
    mentionsOperators = [],
    channelInfo = null,
  }) {
    if (!channelId) {
      throw new Error("Selecione um canal ou conversa para enviar a mensagem.");
    }
    if (!content?.trim() && (!files || files.length === 0)) {
      throw new Error("Digite uma mensagem ou anexe um arquivo para enviar.");
    }

    try {
      const senderName = activeOperator
        ? `${activeOperator.nome} (${user?.name || "Terminal"})`
        : user?.name || user?.username || "Usuário";

      const senderColor = activeOperator?.cor || "#3b82f6";
      const { orders } = this.parseMessageContent(content);

      let record;

      if (files && files.length > 0) {
        // Envio Multipart (com arquivos)
        const formData = new FormData();
        formData.append("channel", channelId);
        if (user?.id) {
          formData.append("sender_user", user.id);
        }
        if (activeOperator?.id) {
          formData.append("sender_operator", activeOperator.id);
        }
        formData.append("sender_name", senderName);
        formData.append("sender_color", senderColor);
        formData.append("content", content || "");

        if (replyToId) formData.append("reply_to", replyToId);
        if (orders.length > 0) {
          formData.append("referenced_orders", JSON.stringify(orders));
        }

        mentionsUsers.forEach((uId) => formData.append("mentions_users", uId));
        mentionsOperators.forEach((opId) => formData.append("mentions_operators", opId));

        for (const file of files) {
          formData.append("attachments", file);
        }

        record = await pb.collection("chat_messages").create(formData);
      } else {
        // Envio JSON simples
        const payload = {
          channel: channelId,
          sender_name: senderName,
          sender_color: senderColor,
          content: content || "",
          referenced_orders: orders,
          mentions_users: mentionsUsers,
          mentions_operators: mentionsOperators,
          reply_to: replyToId || null,
        };

        if (user?.id) {
          payload.sender_user = user.id;
        }

        if (activeOperator?.id) {
          payload.sender_operator = activeOperator.id;
        }

        record = await pb.collection("chat_messages").create(payload);
      }

      // Atualiza timestamp do canal para ordenar recentes
      pb.collection("chat_channels").update(channelId, { updated: new Date().toISOString() }).catch(() => {});

      // --- DISPARAR NOTIFICAÇÕES DE MENÇÕES ---
      const channelLabel = channelInfo?.display_name || `#${channelInfo?.name || "chat"}`;

      for (const targetUserId of mentionsUsers) {
        if (targetUserId !== user?.id) {
          notificationService.createNotification({
            recipient_user: targetUserId,
            type: "mention",
            title: `${senderName} mencionou você no ${channelLabel}`,
            message: content,
            link_tab: channelInfo?.module_key || "chat",
            link_message_id: record.id,
          });
        }
      }

      for (const targetOpId of mentionsOperators) {
        if (targetOpId !== activeOperator?.id) {
          notificationService.createNotification({
            recipient_operator: targetOpId,
            type: "mention",
            title: `${senderName} mencionou você no ${channelLabel}`,
            message: content,
            link_tab: channelInfo?.module_key || "chat",
            link_message_id: record.id,
          });
        }
      }

      // Se for DM, notifica o outro participante
      if (channelInfo?.type === "direct") {
        const otherUsers = (channelInfo.members_users || []).filter((id) => id !== user?.id);
        const otherOps = (channelInfo.members_operators || []).filter((id) => id !== activeOperator?.id);

        otherUsers.forEach((targetId) => {
          notificationService.createNotification({
            recipient_user: targetId,
            type: "direct_message",
            title: `Nova mensagem direta de ${senderName}`,
            message: content || "Enviou um anexo",
            link_tab: "chat",
            link_id: channelId,
            link_message_id: record.id,
          });
        });

        otherOps.forEach((targetId) => {
          notificationService.createNotification({
            recipient_operator: targetId,
            type: "direct_message",
            title: `Nova mensagem direta de ${senderName}`,
            message: content || "Enviou um anexo",
            link_tab: "chat",
            link_id: channelId,
            link_message_id: record.id,
          });
        });
      }

      return record;
    } catch (error) {
      console.error("❌ [chatService] Erro ao enviar mensagem:", error);
      throw error;
    }
  },

  // --- 7. CRIAR OU OBTER CANAL DE DM 1:1 ---
  async getOrCreateDM({ currentUser, currentOperator, targetUser, targetOperator }) {
    try {
      const userIds = [currentUser?.id, targetUser?.id].filter(Boolean).sort();
      const opIds = [currentOperator?.id, targetOperator?.id].filter(Boolean).sort();

      const dmName = `dm_${userIds.join("_")}_${opIds.join("_")}`;

      // Tenta buscar canal existente
      const existing = await pb.collection("chat_channels").getFullList({
        filter: `name = "${dmName}"`,
      }).catch(() => []);

      if (existing.length > 0) {
        return existing[0];
      }

      // Monta nome de exibição
      const displayName = targetOperator
        ? `${targetOperator.nome} (Operador)`
        : targetUser?.name || targetUser?.username || "Conversa Direta";

      const newChannel = await pb.collection("chat_channels").create({
        name: dmName,
        display_name: displayName,
        type: "direct",
        is_private: true,
        members_users: userIds,
        members_operators: opIds,
        icon: "User",
      });

      return newChannel;
    } catch (error) {
      console.error("❌ [chatService] Erro ao criar DM:", error);
      throw error;
    }
  },

  // --- 9. CRIAR OU OBTER CANAL COM O ASSISTENTE DE IA (ISOLADO POR OPERADOR/USUÁRIO) ---
  async getOrCreateAIChannel({ currentUser, currentOperator }) {
    try {
      const opId = currentOperator?.id;
      const userId = currentUser?.id;

      // Escopo estrito: operador ativo (se houver) ou usuário autenticado
      const companionKey = opId ? `op_${opId}` : `user_${userId || "anon"}`;
      const channelName = `ai_companion_${companionKey}`;

      const existing = await pb.collection("chat_channels").getFullList({
        filter: `name = "${channelName}"`,
      }).catch(() => []);

      if (existing.length > 0) {
        return existing[0];
      }

      const ownerName = currentOperator?.nome || currentUser?.name || "Operador";
      const newChannel = await pb.collection("chat_channels").create({
        name: channelName,
        display_name: "Assistente Sempre Joias ✨",
        type: "ai",
        is_private: true,
        members_users: userId ? [userId] : [],
        members_operators: opId ? [opId] : [],
        icon: "Sparkles",
        description: `Assistente de IA pessoal de ${ownerName}.`,
      });

      return newChannel;
    } catch (e) {
      console.error("❌ [chatService] Erro ao criar canal de IA:", e);
      throw e;
    }
  },

  // --- 10. LIMPAR TODAS AS MENSAGENS DE UM CANAL ---
  async clearChannelMessages(channelId) {
    if (!channelId) return false;
    try {
      const messages = await pb.collection("chat_messages").getFullList({
        filter: `channel = "${channelId}"`,
      }).catch(() => []);

      await Promise.allSettled(
        messages.map((msg) => pb.collection("chat_messages").delete(msg.id).catch(() => {}))
      );
      return true;
    } catch (e) {
      console.warn("❌ [chatService] Aviso ao limpar mensagens do canal:", e);
      return false;
    }
  },

  // --- 8. HELPER PARA URL DE ANEXO ---
  getFileUrl(record, filename, thumb = null) {
    if (!record || !filename) return "";
    try {
      return pb.files.getUrl(record, filename, thumb ? { thumb } : {});
    } catch (e) {
      return "";
    }
  },
};
